import { useState, useEffect } from "react";
import "./Layout.css";

// ─────────────────────────────────────────────
// localStorage helpers
// ─────────────────────────────────────────────
const COOKIE_KEY = "ortho_cookie_prefs";

const defaultPrefs = {
  necessary: true,
  functional: false,
  analytics: false,
  performance: false,
  advertisement: false,
};

const loadPrefs = () => {
  try {
    const s = localStorage.getItem(COOKIE_KEY);
    if (s) {
      // console.log("✅ Cookie prefs already stored in browser:", JSON.parse(s));
      return JSON.parse(s);
    } else {
      // console.log("🆕 No cookie prefs found — first time user");
      return null;
    }
  } catch {
    return null;
  }
};

const savePrefs = (prefs) => {
  localStorage.setItem(COOKIE_KEY, JSON.stringify({ ...prefs, necessary: true }));
  // console.log("💾 Cookie prefs saved to browser:", prefs);
};

// ─────────────────────────────────────────────
// Cookie SVG Icon
// ─────────────────────────────────────────────
const CookieIcon = () => (
  <svg
    className="cc-icon"
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 64 64"
    width="26"
    height="26"
    fill="white"
  >
    <path d="M32 4C16.536 4 4 16.536 4 32s12.536 28 28 28 28-12.536 28-28
      c0-1.07-.063-2.126-.184-3.164a4 4 0 0 1-4.53-6.53
      4 4 0 0 1-6.53-4.53A27.9 27.9 0 0 0 32 4zm0 4
      c3.03 0 5.94.488 8.668 1.382a8 8 0 0 0 8.95 10.598
      8 8 0 0 0 8.598 8.95A24.047 24.047 0 0 1 56 32
      c0 13.255-10.745 24-24 24S8 45.255 8 32 18.745 8 32 8z"/>
    <circle cx="20" cy="22" r="3"/>
    <circle cx="34" cy="16" r="2"/>
    <circle cx="44" cy="30" r="3"/>
    <circle cx="26" cy="36" r="4"/>
    <circle cx="38" cy="44" r="2.5"/>
    <circle cx="18" cy="42" r="2"/>
  </svg>
);

// ─────────────────────────────────────────────
// Accordion Item
// ─────────────────────────────────────────────
const AccordionItem = ({ title, description, id, checked, disabled, onChange }) => {
  const [open, setOpen] = useState(false);

  return (
    <div style={{ borderBottom: "1px solid #e4edf2" }}>
      {/* Header */}
      <div
        className="cc-acc-header"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "13px 6px",
          cursor: "pointer",
          userSelect: "none",
          transition: "background 0.15s",
        }}
        onClick={() => setOpen((o) => !o)}
      >
        {/* Left — arrow + title */}
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <span
            style={{
              fontSize: "0.68rem",
              color: "#0077a8",
              display: "inline-block",
              transition: "transform 0.25s cubic-bezier(0.34,1.56,0.64,1)",
              transform: open ? "rotate(90deg)" : "rotate(0deg)",
            }}
          >
            ▶
          </span>
          <strong style={{ fontSize: "0.93rem", color: "#0d2d45", fontWeight: 600 }}>
            {title}
          </strong>
        </div>

        {/* Right — badge + toggle */}
        <div
          style={{ display: "flex", alignItems: "center", gap: "12px", flexShrink: 0 }}
          onClick={(e) => e.stopPropagation()}
        >
          {disabled && (
            <span
              style={{
                fontSize: "0.72rem",
                fontWeight: 700,
                color: "#00a86b",
                background: "#e6f9f1",
                padding: "3px 10px",
                borderRadius: "20px",
              }}
            >
              Always Active
            </span>
          )}

          {/* Toggle switch */}
          <div
            style={{
              position: "relative",
              width: "44px",
              height: "24px",
              borderRadius: "24px",
              flexShrink: 0,
              background: checked ? (disabled ? "#00a86b" : "#0077a8") : "#c5d8e4",
              cursor: disabled ? "not-allowed" : "pointer",
              transition: "background 0.3s ease",
              boxShadow: checked ? "0 2px 8px rgba(0,119,168,0.3)" : "none",
            }}
            onClick={() => !disabled && onChange(id, !checked)}
          >
            <div
              style={{
                position: "absolute",
                top: "4px",
                left: "4px",
                width: "16px",
                height: "16px",
                borderRadius: "50%",
                background: "#fff",
                boxShadow: "0 1px 5px rgba(0,0,0,0.25)",
                transition: "transform 0.3s cubic-bezier(0.34,1.56,0.64,1)",
                transform: checked ? "translateX(20px)" : "translateX(0px)",
              }}
            />
          </div>
        </div>
      </div>

      {/* Body (accordion content) */}
      {open && <p className="cc-acc-body">{description}</p>}
    </div>
  );
};

// ─────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────
const CookieConsent = ({ onPreferencesChange }) => {
  const [visible,  setVisible]  = useState(false);
  const [showMore, setShowMore] = useState(false);
  const [prefs,    setPrefs]    = useState(defaultPrefs);
  const [tooltip,  setTooltip]  = useState(false);

  // On mount — show popup only if no prefs saved yet
  useEffect(() => {
    const saved = loadPrefs();
    if (!saved) {
      // No prefs saved ever → show popup
      setVisible(true);
    } else {
      // Already saved before → load silently, no popup
      setPrefs(saved);
      onPreferencesChange?.(saved);
    }
  }, []);

  const handleToggle = (id, val) => setPrefs((p) => ({ ...p, [id]: val }));

  // ✅ X button — just closes, no changes
  // If first time user closes without choosing → save necessary only
  const handleClose = () => {
    const saved = loadPrefs();
    if (!saved) {
      // First time user closed without choosing → save necessary only
      savePrefs(defaultPrefs);
      onPreferencesChange?.(defaultPrefs);
      // console.log("🔒 Modal closed without choosing — saved necessary only");
    } else {
      // console.log("❎ Modal closed — keeping existing prefs");
    }
    setVisible(false);
  };

  const handleRejectAll = () => {
    const r = {
      necessary: true,
      functional: false,
      analytics: false,
      performance: false,
      advertisement: false,
    };
    setPrefs(r);
    savePrefs(r);
    onPreferencesChange?.(r);
    setVisible(false);
    // console.log("❌ User rejected all cookies");
  };

  const handleSave = () => {
    savePrefs(prefs);
    onPreferencesChange?.(prefs);
    setVisible(false);
    // console.log("💾 User saved custom cookie preferences:", prefs);
  };

  const handleAcceptAll = () => {
    const a = {
      necessary: true,
      functional: true,
      analytics: true,
      performance: true,
      advertisement: true,
    };
    setPrefs(a);
    savePrefs(a);
    onPreferencesChange?.(a);
    setVisible(false);
    // console.log("✅ User accepted all cookies");
  };

  const openModal = () => {
    const saved = loadPrefs();
    if (saved) setPrefs(saved);
    setShowMore(false);
    setVisible(true);
  };

  const categories = [
    {
      id: "necessary",
      title: "Necessary",
      disabled: true,
      description:
        "Necessary cookies are required to enable the basic features of this site, such as providing secure log-in or adjusting your consent preferences. These cookies do not store any personally identifiable data.",
    },
    {
      id: "functional",
      title: "Functional",
      description:
        "Functional cookies help perform certain functionalities like sharing the content of the website on social media platforms, collecting feedback, and other third-party features.",
    },
    {
      id: "analytics",
      title: "Analytics",
      description:
        "Analytical cookies are used to understand how visitors interact with the website. These cookies help provide information on metrics such as the number of visitors, bounce rate, traffic source, etc.",
    },
    {
      id: "performance",
      title: "Performance",
      description:
        "Performance cookies are used to understand and analyse the key performance indexes of the website which helps in delivering a better user experience for the visitors.",
    },
    {
      id: "advertisement",
      title: "Advertisement",
      description:
        "Advertisement cookies are used to provide visitors with customised advertisements based on the pages you visited previously and to analyse the effectiveness of the ad campaigns.",
    },
  ];

  return (
    <>
      {/* ── Floating Button + Right Tooltip ── */}
      <div
        className="cc-float-wrap"
        onMouseEnter={() => setTooltip(true)}
        onMouseLeave={() => setTooltip(false)}
      >
        <button
          className="cc-float-btn"
          onClick={openModal}
          aria-label="Open Cookie Settings"
        >
          <CookieIcon />
        </button>

        {tooltip && (
          <div className="cc-tooltip-label">Consent Preferences</div>
        )}
      </div>

      {/* ── Modal ── */}
      {visible && (
        <div className="cc-overlay">
          <div className="cc-modal" role="dialog" aria-modal="true">

            {/* Header */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "24px 26px 0",
              }}
            >
              <h2
                style={{
                  fontSize: "1.18rem",
                  fontWeight: 700,
                  color: "#0d2d45",
                  margin: 0,
                  fontFamily: "Georgia, serif",
                }}
              >
                Customise Consent Preferences
              </h2>

              {/* ✅ FIXED — X just closes, no reject */}
              <button
                className="cc-close-btn"
                onClick={handleClose}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: "1.1rem",
                  cursor: "pointer",
                  color: "#9bb5c4",
                  padding: "4px 8px",
                  borderRadius: "7px",
                  lineHeight: 1,
                  transition: "background 0.2s, color 0.2s",
                }}
              >
                ✕
              </button>
            </div>

            {/* Body */}
            <div style={{ padding: "16px 26px 0" }}>
              <p
                style={{
                  fontSize: "0.84rem",
                  color: "#4a6070",
                  lineHeight: 1.65,
                  marginBottom: "10px",
                }}
              >
                We use cookies to help you navigate efficiently and perform certain functions. You
                will find detailed information about all cookies under each consent category below.
              </p>
              <p
                style={{
                  fontSize: "0.84rem",
                  color: "#4a6070",
                  lineHeight: 1.65,
                  marginBottom: "10px",
                }}
              >
                The cookies categorised as <strong>"Necessary"</strong> are stored on your browser
                as they are essential for enabling the basic functionalities of the site.{" "}
                {!showMore && (
                  <>
                    ...{" "}
                    <button
                      className="cc-show-more"
                      style={{
                        background: "none",
                        border: "none",
                        color: "#0077a8",
                        fontWeight: 600,
                        fontSize: "0.84rem",
                        cursor: "pointer",
                        textDecoration: "underline",
                        padding: 0,
                        transition: "color 0.2s",
                      }}
                      onClick={() => setShowMore(true)}
                    >
                      Show more
                    </button>
                  </>
                )}
              </p>

              {showMore && (
                <p
                  style={{
                    fontSize: "0.84rem",
                    color: "#4a6070",
                    lineHeight: 1.65,
                    marginBottom: "10px",
                  }}
                >
                  We also use third-party cookies that help us analyse how you use this website.
                  These cookies will only be stored in your browser with your prior consent. You
                  have the option to opt-out — however, opting out may affect your browsing
                  experience.{" "}
                  <button
                    className="cc-show-more"
                    style={{
                      background: "none",
                      border: "none",
                      color: "#0077a8",
                      fontWeight: 600,
                      fontSize: "0.84rem",
                      cursor: "pointer",
                      textDecoration: "underline",
                      padding: 0,
                    }}
                    onClick={() => setShowMore(false)}
                  >
                    Show less
                  </button>
                </p>
              )}

              {/* Accordion list */}
              <div style={{ marginTop: "14px", borderTop: "1px solid #e4edf2" }}>
                {categories.map((cat) => (
                  <AccordionItem
                    key={cat.id}
                    {...cat}
                    checked={prefs[cat.id]}
                    onChange={handleToggle}
                  />
                ))}
              </div>
            </div>

            {/* Footer */}
            <div
              style={{
                display: "flex",
                gap: "10px",
                padding: "20px 26px 26px",
                flexWrap: "wrap",
              }}
            >
              <button className="cc-btn cc-btn-outline" onClick={handleRejectAll}>
                Reject All
              </button>
              <button className="cc-btn cc-btn-ghost" onClick={handleSave}>
                Save My Preferences
              </button>
              <button className="cc-btn cc-btn-primary" onClick={handleAcceptAll}>
                Accept All
              </button>
            </div>

          </div>
        </div>
      )}
    </>
  );
};

export default CookieConsent;